/* eslint-disable react-hooks/exhaustive-deps */
import { useCallback, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import TableCell from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import { connect, useSelector } from 'react-redux';

import { getLabelMessage } from '@gef-ui/localization/messages';

import CollapsibleCard from 'components/atoms/GefMui/CollapsibleCard';
import CollapsibleTable from 'components/atoms/GefMui/CollapsibleTable';
import { ComponentLoader } from 'components/index';
import fetch from 'features/common/fetch';
import { getVisibleStateById } from 'features/common/Subheader/selectors';
import campaignNominations from 'features/views/campaignNominations';
import { deleteNomination, fetchSellers, updateNominations } from 'features/views/campaignSubsellerNomination/actions';

import ManagerItemHeader from './ManagerItemHeader';
import Seller from './Seller';

const Manager = (props) => {
	const {
		manager,
		count,
		campaignId,
		channelId,
		regionId,
		isTheOnlyManager,
		agencyId,
		fetchSellers,
		fetchNominatedSellers,
		fetchNominationsCount,
		deleteNomination,
		updateNominations,
		updateManagerCounts,
	} = props;

	const [countsByLayer, setCountsByLayer] = useState({});
	const [sellers, setSellers] = useState([]);
	const [nominatedSellers, setNominatedSellers] = useState({});

	const visibleState = useSelector((state) => getVisibleStateById(state, manager.login, isTheOnlyManager));

	const updateSellerCounts = useCallback(() => {
		updateManagerCounts();
		fetchNominationsCount(campaignId, {
			manager: manager.login,
			regionId,
			agencyIds: [agencyId],
		}).then((nominationCountsByLayer) => {
			setCountsByLayer(nominationCountsByLayer);
		});
	}, [fetchNominationsCount, campaignId, manager.login, regionId, agencyId, setCountsByLayer]);

	useEffect(() => {
		if (visibleState) {
			updateSellerCounts();
		}
	}, [visibleState]);

	const handleReload = useCallback(() => {
		updateSellerCounts();
	}, [campaignId]);

	const handleToggleSellerNomination = useCallback(
		(seller) => {
			if (nominatedSellers[seller.login]) {
				return deleteNomination(campaignId, seller.login).then(() => {
					setNominatedSellers((value) => ({ ...value, [seller.login]: false }));
					updateSellerCounts();
				});
			} else {
				return updateNominations(campaignId, seller.login).then(() => {
					setNominatedSellers((value) => ({ ...value, [seller.login]: true }));
					updateSellerCounts();
				});
			}
		},
		[nominatedSellers, setNominatedSellers],
	);

	const handleSubsellerHasBeenNominated = useCallback(
		(seller) => {
			if (!nominatedSellers[seller.login]) {
				updateNominations(campaignId, seller.login).then(() => {
					setNominatedSellers((value) => ({ ...value, [seller.login]: true }));
					updateSellerCounts();
				});
			}
		},
		[nominatedSellers, updateNominations, setNominatedSellers, updateSellerCounts],
	);

	useEffect(() => {
		if (visibleState) {
			fetchSellers(channelId, regionId, agencyId, manager.login).then(({ body }) => {
				setSellers(body);
			});
			fetchNominatedSellers(campaignId, channelId, regionId, agencyId, manager.login).then(({ body }) => {
				const nominatedSellersForManagers = {};
				body.forEach((nomination) => {
					if (!nomination.ppz) {
						nominatedSellersForManagers[nomination.seller.login] = true;
					}
				});
				setNominatedSellers(nominatedSellersForManagers);
			});
		}
	}, [channelId, regionId, agencyId, manager.login, visibleState, fetchNominatedSellers, setNominatedSellers]);

	return (
		<CollapsibleCard
			id={manager.login}
			initiallyOpen={isTheOnlyManager}
			header={<ManagerItemHeader manager={manager} count={count} />}
		>
			<ComponentLoader
				fetchStorePath={`${fetch.constants.urls.campaignNominationsCount}.manager.${manager.login}`}
				onReload={handleReload}
			>
				<CollapsibleTable
					tableId="ManagerNominationsTable"
					tableHeader={
						<TableHead>
							<TableRow
								sx={{
									'& th': {
										backgroundColor: 'transparent',
										py: 2,
									},
								}}
							>
								<TableCell sx={{ pl: 7.5 }}>
									{getLabelMessage('campaignSubsellerNomination:tableSellerHeader')}
								</TableCell>
								<TableCell sx={{ pr: 5 }} align="right" colSpan={2}>
									{getLabelMessage('campaignSubsellerNomination:tableSubsellerHeader')}
								</TableCell>
							</TableRow>
						</TableHead>
					}
					tableBody={sellers.map((seller, i) => (
						<Seller
							id={seller.login}
							key={i}
							seller={seller}
							nominatedSellers={nominatedSellers}
							manager={manager}
							channelId={channelId}
							campaignId={campaignId}
							agencyId={agencyId}
							regionId={regionId}
							count={countsByLayer[seller.login]}
							updateSellerCounts={updateSellerCounts}
							toggleSellerNomination={handleToggleSellerNomination}
							subsellerHasBeenNominatedForSeller={handleSubsellerHasBeenNominated}
						/>
					))}
				/>
			</ComponentLoader>
		</CollapsibleCard>
	);
};

Manager.propTypes = {
	agencyId: PropTypes.number,
	campaignId: PropTypes.number,
	channelId: PropTypes.number,
	count: PropTypes.number,
	deleteNomination: PropTypes.func,
	fetchNominatedSellers: PropTypes.func,
	fetchNominationsCount: PropTypes.func,
	fetchSellers: PropTypes.func,
	isTheOnlyManager: PropTypes.bool,
	manager: PropTypes.object,
	regionId: PropTypes.number,
	updateManagerCounts: PropTypes.func,
	updateNominations: PropTypes.func,
};

export default connect(null, {
	fetchSellers,
	updateNominations,
	deleteNomination,
	fetchNominatedSellers: campaignNominations.actions.fetchNominatedSellers,
	fetchNominationsCount: campaignNominations.actions.fetchNominationsCount,
})(Manager);
